import { safeJsonObject, safeJsonParse } from '../lib/safeJson'
/**
 * 数字显示格式（backlog #7：公式结果格式化）。
 *
 * 规则（用户 2026-10-22 指定的三项）：
 *   thousands  千分位（1234567 → 1,234,567）
 *   percent    百分比（**数值 × 100 后加 %**，即 0.5 → 50.0%）
 *   decimals   小数位（0–4）
 *
 * 为什么百分比要乘 100：公式引擎算的是纯数字（0.5 就是 0.5），
 * 乘 100 才是数学上正确的百分比。如果公式本身已经算成 50 这种"百分数"，
 * 就不要勾百分比 —— 这条在弹框里也写清楚了，避免误解。
 *
 * 存到列的 settings.numFormat（落库，不是前端存储）。
 */

export interface NumFormat {
  thousands?: boolean
  percent?: boolean
  decimals?: number
}

/** 从列 settings（可能是对象也可能是 JSON 文本）里读格式 */
export function readNumFormat(settings: unknown): NumFormat {
  const obj: unknown =
    typeof settings === 'string' ? safeJsonParse<unknown>(settings, null, 'numFormat') : settings
  if (obj === null || typeof obj !== 'object') return {}
  const f = (obj as Record<string, unknown>).numFormat
  if (f === null || typeof f !== 'object') return {}
  const raw = f as Record<string, unknown>
  const out: NumFormat = {}
  if (raw.thousands === true) out.thousands = true
  if (raw.percent === true) out.percent = true
  if (typeof raw.decimals === 'number' && Number.isFinite(raw.decimals)) {
    out.decimals = Math.max(0, Math.min(4, Math.round(raw.decimals)))
  }
  return out
}

/** 把格式写回列 settings（返回序列化后的 JSON） */
export function writeNumFormat(settings: unknown, fmt: NumFormat): string {
  const obj: unknown =
    typeof settings === 'string' ? safeJsonObject<Record<string, unknown>>(settings, {}, 'numFormat') : settings
  const base: Record<string, unknown> =
    obj !== null && typeof obj === 'object' ? { ...(obj as Record<string, unknown>) } : {}

  const next: Record<string, unknown> = {}
  if (fmt.thousands === true) next.thousands = true
  if (fmt.percent === true) next.percent = true
  if (typeof fmt.decimals === 'number') next.decimals = Math.max(0, Math.min(4, Math.round(fmt.decimals)))

  if (Object.keys(next).length === 0) delete base.numFormat
  else base.numFormat = next
  return JSON.stringify(base)
}

/**
 * 格式化一个单元格文本。
 * 非数字（空、`#REF!`、`#CYCLE!` 这类错误）原样返回，不干扰错误显示。
 */
export function formatNumberValue(raw: string, fmt: NumFormat): string {
  const text = raw.trim()
  if (text === '') return raw
  const n = Number(text)
  if (!Number.isFinite(n)) return raw

  const value = fmt.percent === true ? n * 100 : n
  const decimals = typeof fmt.decimals === 'number' ? fmt.decimals : undefined

  let body: string
  if (decimals === undefined) {
    // 没指定小数位：保留有效数字，但要**去掉浮点运算的尾巴**
    // （0.1 * 100 会算出 10.000000000000002，toFixed(10)+parseFloat 仍会留下 123456789.10000001）
    if (Number.isInteger(value)) {
      body = String(value)
    } else {
      // 12 位有效数字足够覆盖常见业务数字，同时能把二进制浮点的尾巴抹掉
      body = String(Number(value.toPrecision(12)))
    }
  } else {
    body = value.toFixed(decimals)
  }

  if (fmt.thousands === true) {
    const neg = body.startsWith('-')
    const plain = neg ? body.slice(1) : body
    const parts = plain.split('.')
    const intPart = parts[0] ?? '0'
    const fracPart = parts[1]
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    body = `${neg ? '-' : ''}${grouped}${fracPart === undefined ? '' : `.${fracPart}`}`
  }

  return fmt.percent === true ? `${body}%` : body
}
